/**
 * Competition Entry Management types (ADR-028 / ADR-031 P3).
 * Entry = who may compete; Player = participant identity (not a permission role).
 * Stored under arenas/{id}/events/{eventId}/entries (Event-owned).
 */

import { newEntryId } from "./competitionTypes";

/**
 * @typedef {'pending'|'confirmed'|'withdrawn'|'cancelled'} EntryStatus
 */

/**
 * @typedef {'singles'|'doubles'|'mixed_doubles'} EntryCategory
 */

/**
 * Player participant snapshot on an Entry (not a User Account permission).
 * @typedef {Object} EntryPlayerSnapshot
 * @property {string | null} playerId
 * @property {string} firstName
 * @property {string} lastName
 * @property {'male'|'female'|'other'|'unspecified'|string} [gender]
 * @property {number | null} [dateOfBirthMs]
 * @property {string} [club]
 * @property {string} [state]
 * @property {string} [country]
 * @property {string} [contactNumber]
 * @property {string} [email]
 * @property {string | null} [linkedUserId]
 */

/**
 * Managed Competition Entry linked to an Event.
 * Compatible with ADR Entry root via entryId, entryType, memberPlayerIds, label.
 * @typedef {Object} ManagedCompetitionEntry
 * @property {string} entryId
 * @property {string} competitionId
 * @property {string} eventId
 * @property {EntryCategory} category
 * @property {'player'|'pair'} entryType
 * @property {EntryStatus} status
 * @property {string} label
 * @property {string[]} memberPlayerIds
 * @property {EntryPlayerSnapshot[]} members
 * @property {string} [sport]
 * @property {number} [seed]
 * @property {number} createdAtMs
 * @property {number} updatedAtMs
 * @property {string} createdBy  User Account uid (organizer)
 */

export const ENTRY_STATUSES = Object.freeze({
  PENDING: "pending",
  CONFIRMED: "confirmed",
  WITHDRAWN: "withdrawn",
  CANCELLED: "cancelled",
});

export const ENTRY_CATEGORIES = Object.freeze({
  SINGLES: "singles",
  DOUBLES: "doubles",
  MIXED_DOUBLES: "mixed_doubles",
});

export const ENTRY_CATEGORY_LABELS = Object.freeze({
  singles: "Singles",
  doubles: "Doubles",
  mixed_doubles: "Mixed Doubles",
});

export const ENTRY_STATUS_LABELS = Object.freeze({
  pending: "Pending",
  confirmed: "Confirmed",
  withdrawn: "Withdrawn",
  cancelled: "Cancelled",
});

/**
 * @param {EntryCategory} category
 * @returns {'player'|'pair'}
 */
export function entryTypeForCategory(category) {
  if (category === ENTRY_CATEGORIES.SINGLES) return "player";
  return "pair";
}

/**
 * @param {EntryCategory} category
 */
export function expectedMemberCount(category) {
  return category === ENTRY_CATEGORIES.SINGLES ? 1 : 2;
}

/**
 * @param {EntryPlayerSnapshot} member
 */
export function memberDisplayName(member) {
  const first = (member?.firstName || "").trim();
  const last = (member?.lastName || "").trim();
  return [first, last].filter(Boolean).join(" ") || "Player";
}

/**
 * @param {EntryPlayerSnapshot[]} members
 * @param {EntryCategory} category
 */
export function buildEntryLabel(members, category) {
  const names = (members || []).map(memberDisplayName);
  if (category === ENTRY_CATEGORIES.SINGLES) return names[0] || "Singles entry";
  return names.filter(Boolean).join(" / ") || "Doubles entry";
}

/**
 * @param {Partial<ManagedCompetitionEntry> & {
 *   competitionId: string,
 *   eventId: string,
 *   category: EntryCategory,
 *   members: EntryPlayerSnapshot[],
 *   createdBy: string,
 * }} input
 * @returns {ManagedCompetitionEntry}
 */
export function createManagedEntryShell(input) {
  const category = input.category || ENTRY_CATEGORIES.SINGLES;
  const members = input.members || [];
  const entryType = entryTypeForCategory(category);
  const now = Date.now();
  return {
    entryId: input.entryId || newEntryId(),
    competitionId: input.competitionId,
    eventId: input.eventId,
    category,
    entryType,
    status: input.status || ENTRY_STATUSES.PENDING,
    label: input.label || buildEntryLabel(members, category),
    memberPlayerIds: members.map((m) => m.playerId).filter(Boolean),
    memberUserIds: [
      ...new Set(members.map((m) => m.linkedUserId).filter(Boolean)),
    ],
    members,
    sport: input.sport || "badminton",
    seed: input.seed ?? null,
    createdAtMs: input.createdAtMs ?? now,
    updatedAtMs: input.updatedAtMs ?? now,
    createdBy: input.createdBy,
  };
}

/**
 * Map managed entry → draw-compatible minimal Entry (for future fixture wiring).
 * @param {ManagedCompetitionEntry} managed
 */
/**
 * One document id per player per event so a retry or a second tap
 * updates the same entry instead of creating another.
 * @param {string} eventId
 * @param {string} playerId
 */
export function selfEntryDocumentId(eventId, playerId) {
  const eventKey = String(eventId || "event").replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 48);
  const playerKey = String(playerId || "player").replace(/[^a-zA-Z0-9_-]/g, "").slice(0, 48);
  return `ent_${eventKey}_${playerKey}`;
}

export function managedEntryToDrawEntry(managed) {
  return {
    entryId: managed.entryId,
    label: managed.label,
    entryType: managed.entryType,
    memberPlayerIds: managed.memberPlayerIds || [],
    seed: managed.seed ?? null,
  };
}
